import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatCurrency } from '../../utils/currencyUtils';
import { SlaStatusIndicator } from '../sla/SlaBadge';
import { useSlaAccess } from '../../utils/slaAccessControl';
import { assignClaimToSelf, releaseClaimFromSelf, selectIsAssigning, selectIsReleasing, selectAssignmentError } from '../../features/claims/claimSlice';
import { useToast } from '../../contexts/ToastContext';
import { CLAIMS_OFFICER } from '../../constants/userRoles';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import useConfirmationDialog from '../../hooks/useConfirmationDialog';
import { LoadingButton, InlineRowLoading, AssignmentActionOverlay } from '../ui/LoadingStates';
import OmButton from '../ui/OmButton';

/**
 * Determines if benefit amount should be visible based on user role and claim approval status
 * @param {Object} claim - The claim object
 * @param {Object} user - The current user object
 * @returns {boolean} - Whether to show the benefit amount
 */
const shouldShowBenefitAmount = (claim, user) => {
  // Get user roles from the user object
  const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
  
  // Claims Officers, Approvers, Finance, and Admins can always see benefit amounts
  const canAlwaysSeeBenefits = userRoles.some(role => 
    ['Claims_Officer', 'Approver', 'Finance', 'Admin'].includes(role)
  );
  
  if (canAlwaysSeeBenefits) {
    return true;
  }
  
  // For HR/CRM/Broker roles, show amount only if it's been approved by Claims Officer
  // Check if benefit has been approved (benefitApprovedAt exists OR approvedBenefitAmount has a value)
  const isBenefitApproved = Boolean(claim.benefitApprovedAt) || Boolean(claim.approvedBenefitAmount);
  
  return isBenefitApproved;
};

const ClaimRow = ({
  claim,
  isClaimsOfficer,
  getStatusBadgeColor,
  getClaimTypeLabel,
  user,
  onTakeClaim,
  onReleaseClaim,
  isAssigning,
  isReleasing,
  navigate
}) => {
  const slaAccess = useSlaAccess(user);
  const currentUserId = user?.userId || user?.id;
  
  // Enhanced permission validation
  const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
  const isActualClaimsOfficer = userRoles.includes(CLAIMS_OFFICER);
  
  // Check if claim is assigned to current user
  const isAssignedToMe = claim.assignedToId === currentUserId;
  const isUnassigned = !claim.assignedToId;
  
  // Check if claim is in a valid status for assignment
  const validStatusesForAssignment = ['SUBMITTED', 'UNDER_REVIEW', 'MISSING_DOCUMENTS'];
  const canAssign = validStatusesForAssignment.includes(claim.status) && isActualClaimsOfficer;

  return (
    <React.Fragment>
      <tr 
        className="hover:bg-fresh-leaf/5 hover:border-fresh-leaf/20 transition-all duration-300 ease-in-out border-b border-silver-mist/10 cursor-pointer hover:shadow-soft"
        onClick={() => navigate(`/claims/${claim.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/claims/${claim.id}`);
          }
        }}
        aria-label={`View details for claim ${claim.id}`}
      >
        <td className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <span className="text-xs sm:text-sm font-bold text-deep-jungle tracking-wide">
              #{claim.id}
            </span>
            {/* Mobile: Show type below ID */}
            <span className="sm:hidden text-xs text-graphite mt-1">
              {getClaimTypeLabel(claim.type)}
            </span>
          </div>
        </td>
        <td className="hidden sm:table-cell px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-xs sm:text-sm font-medium text-graphite leading-relaxed">
              {getClaimTypeLabel(claim.type)}
            </span>
          </div>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5">
          <div className="flex flex-col">
            <span className="text-xs sm:text-sm font-medium text-graphite leading-relaxed truncate">
              {claim.claimantName}
            </span>
            {/* Mobile: Show amount below claimant - conditionally based on role and approval */}
            <span className="md:hidden text-xs font-bold text-forest-fern mt-1">
              {shouldShowBenefitAmount(claim, user) ? formatCurrency(claim.benefitAmount) : '***'}
            </span>
          </div>
        </td>
        <td className="hidden md:table-cell px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-xs sm:text-sm font-bold text-forest-fern tracking-wide">
              {shouldShowBenefitAmount(claim, user) ? formatCurrency(claim.benefitAmount) : '***'}
            </span>
          </div>
        </td>
        <td className="hidden lg:table-cell px-6 py-5 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-sm font-medium text-steel leading-relaxed">
              {new Date(claim.createdAt).toLocaleDateString()}
            </span>
          </div>
        </td>
        
        {/* Enhanced Actions Column - Only for Claims Officers */}
        {isClaimsOfficer && (
          <td className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5">
            <div className="flex items-center justify-end space-x-2">
              {canAssign && (
                <OmButton
                  buttonType={isAssignedToMe ? "text-button" : "filled-button"}
                  buttonSize="small"
                  disabled={isAssigning || isReleasing}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAssignedToMe) {
                      onReleaseClaim(claim.id);
                    } else {
                      onTakeClaim(claim.id);
                    }
                  }}
                  aria-label={isAssignedToMe ? `Release claim ${claim.id}` : `Take claim ${claim.id}`}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid white',
                    color: isAssignedToMe ? '#d97706' : '#10b981'
                  }}
                >
                  {(isAssigning || isReleasing) ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isAssigning ? "Taking..." : "Releasing..."}
                    </span>
                  ) : (
                    <span className="font-medium">
                      {isAssignedToMe ? "Release Claim" : "Take Claim"}
                    </span>
                  )}
                </OmButton>
              )}
              <OmButton
                buttonType="text-button"
                buttonSize="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/claims/${claim.id}`);
                }}
                aria-label={`View details for claim ${claim.id}`}
              >
                <span className="font-medium">View Details</span>
              </OmButton>
              {canAssign && !isUnassigned && !isAssignedToMe && (
                <div className="flex items-center justify-center px-3 py-2">
                  <span className="inline-flex items-center text-xs font-medium text-steel">
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 616 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Assigned to Others</span>
                  </span>
                </div>
              )}
            </div>
          </td>
        )}

      </tr>

    </React.Fragment>
  );
};

const ClaimsTable = ({
  filteredClaims,
  isClaimsOfficer,
  getStatusBadgeColor,
  getClaimTypeLabel,
  user // Add user prop to pass to ClaimRow
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const confirmationDialog = useConfirmationDialog();
  
  // Get assignment loading states
  const isAssigning = useSelector(selectIsAssigning);
  const isReleasing = useSelector(selectIsReleasing);
  const assignmentError = useSelector(selectAssignmentError);
  
  // Handle assignment error notifications
  React.useEffect(() => {
    if (assignmentError) {
      showError(assignmentError);
    }
  }, [assignmentError, showError]);
  
  // Handle taking a claim with confirmation dialog
  const handleTakeClaim = async (claimId) => {
    if (!isClaimsOfficer) {
      showError('Only Claims Officers can assign claims to themselves');
      return;
    }

    const claim = filteredClaims.find(c => c.id === claimId);
    if (!claim) {
      showError('Claim not found');
      return;
    }

    confirmationDialog.confirmAssign(claim, async () => {
      try {
        const result = await dispatch(assignClaimToSelf({ claimId })).unwrap();
        showSuccess(result.message || 'Claim assigned successfully');
        
      } catch (error) {
        console.error('Failed to assign claim:', error);
        showError(error?.userMessage || error?.message || 'Failed to assign claim');
        
        // Re-throw to prevent dialog from closing
        throw error;
      }
    });
  };
  
  // Handle releasing a claim with confirmation dialog
  const handleReleaseClaim = async (claimId) => {
    if (!isClaimsOfficer) {
      showError('Only Claims Officers can release claims from themselves');
      return;
    }

    const claim = filteredClaims.find(c => c.id === claimId);
    if (!claim) {
      showError('Claim not found');
      return;
    }

    confirmationDialog.confirmRelease(claim, async () => {
      try {
        const result = await dispatch(releaseClaimFromSelf({ claimId })).unwrap();
        showSuccess(result.message || 'Claim released successfully');
        
      } catch (error) {
        console.error('Failed to release claim:', error);
        showError(error?.userMessage || error?.message || 'Failed to release claim');
        
        // Re-throw to prevent dialog from closing
        throw error;
      }
    });
  };
  if (filteredClaims.length === 0) {
    return (
      <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20">
        <div className="px-4 sm:px-8 py-12 sm:py-16 text-center">
          <div className="flex flex-col items-center space-y-4">
            <svg className="h-12 w-12 sm:h-16 sm:w-16 text-steel/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="space-y-2">
              <h3 className="text-lg sm:text-xl font-semibold text-graphite">No claims found</h3>
              <p className="text-sm sm:text-base text-steel">
                No claims available at the moment
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20 overflow-hidden">
      {/* Mobile-first responsive warning */}
      <div className="block sm:hidden bg-fresh-leaf/10 border-b border-fresh-leaf/20 px-4 py-3">
        <div className="flex items-center space-x-2 text-sm text-woodland-moss">
          <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium">Scroll horizontally to view all columns</span>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-silver-mist/50 scrollbar-track-crystal-white hover:scrollbar-thumb-silver-mist/70">
        <table 
          id="claims-table"
          className="min-w-full divide-y divide-silver-mist/30 lg:min-w-0 w-full" 
          role="table"
          aria-label="Claims list with assignment actions"
          aria-describedby="claims-table-description"
        >
          <caption className="sr-only" id="claims-table-description">
            List of insurance claims with their details and assignment options. Use arrow keys to navigate and Enter to interact with buttons.
          </caption>
          <thead className="bg-deep-jungle">
            <tr>
              <th className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5 text-left text-xs sm:text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                <div className="flex items-center space-x-2">
                  <span>ID</span>
                </div>
              </th>
              <th className="hidden sm:table-cell px-4 lg:px-6 py-4 lg:py-5 text-left text-xs sm:text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                <div className="flex items-center space-x-2">
                  <span>Type</span>
                </div>
              </th>
              <th className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5 text-left text-xs sm:text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                <div className="flex items-center space-x-2">
                  <span>Claimant</span>
                </div>
              </th>
              <th className="hidden md:table-cell px-4 lg:px-6 py-4 lg:py-5 text-left text-xs sm:text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                <div className="flex items-center space-x-2">
                  <span>Amount</span>
                </div>
              </th>
              <th className="hidden lg:table-cell px-6 py-5 text-left text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                <div className="flex items-center space-x-2">
                  <span>Date Created</span>
                </div>
              </th>
              {/* Actions Column - Only for Claims Officers */}
              {isClaimsOfficer && (
                <th className="px-3 sm:px-4 lg:px-6 py-4 lg:py-5 text-right text-xs sm:text-sm font-bold text-crystal-white tracking-wide border-b border-crystal-white/10">
                  <div className="flex items-center justify-end space-x-2">
                    <span>Actions</span>
                  </div>
                </th>
              )}

            </tr>
          </thead>
          <tbody className="bg-crystal-white divide-y divide-silver-mist/20">
            {filteredClaims.map((claim) => (
              <ClaimRow
                key={claim.id}
                claim={claim}
                isClaimsOfficer={isClaimsOfficer}
                getStatusBadgeColor={getStatusBadgeColor}
                getClaimTypeLabel={getClaimTypeLabel}
                user={user}
                onTakeClaim={handleTakeClaim}
                onReleaseClaim={handleReleaseClaim}
                isAssigning={isAssigning}
                isReleasing={isReleasing}
                navigate={navigate}
              />
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.dialogState.isOpen}
        type={confirmationDialog.dialogState.type}
        claimData={confirmationDialog.dialogState.claimData}
        title={confirmationDialog.dialogState.title}
        message={confirmationDialog.dialogState.message}
        confirmText={confirmationDialog.dialogState.confirmText}
        cancelText={confirmationDialog.dialogState.cancelText}
        severity={confirmationDialog.dialogState.severity}
        isLoading={confirmationDialog.dialogState.isLoading}
        onConfirm={confirmationDialog.handleConfirm}
        onClose={confirmationDialog.handleCancel}
      />
      
      {/* Assignment Action Overlay for full-screen loading feedback */}
      <AssignmentActionOverlay
        isVisible={isAssigning || isReleasing}
        operation={isAssigning ? 'assigning' : 'releasing'}
        claimId={confirmationDialog.dialogState.claimData?.id}
      />
    </div>
  );
};

export default ClaimsTable;
